import React, { useState, useEffect } from 'react';
import { Upload, File, FileText, Image as ImageIcon, Trash2, Download, FolderOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { getBackendUrl } from '../utils/getBackendUrl';
import { toast } from 'sonner';
import axios from 'axios';

const API = getBackendUrl() + '/api';

export const PersonalFileStorage = ({ user }) => {
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });

  useEffect(() => {
    fetchFiles();
    fetchStats();
  }, []);

  const fetchFiles = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/personal-files`);
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Failed to load files');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/personal-files/stats/${user.id}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const closeDialog = () => {
    console.log('closeDialog called');
    setIsUploadOpen(false);
    setUploadFiles([]);
    setUploadDescription('');
    setIsUploading(false);
    setUploadProgress({ current: 0, total: 0, fileName: '' });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!uploadFiles || uploadFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    // Validate file sizes
    for (let file of uploadFiles) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 25MB`);
        return;
      }
    }

    setIsUploading(true);
    setUploadProgress({ current: 0, total: uploadFiles.length, fileName: '' });

    try {
      let successCount = 0;
      let failedFiles = [];
      
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        
        // Update progress BEFORE starting upload
        setUploadProgress({ current: i, total: uploadFiles.length, fileName: file.name });
        
        // Small delay to let UI update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
          console.log('Uploading file:', file.name, 'Type:', file.type, 'Size:', file.size);
          
          const formData = new FormData();
          formData.append('file', file);
          if (uploadDescription) {
            formData.append('description', uploadDescription);
          }

          const response = await axios.post(`${API}/personal-files`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000, // 60 second timeout for large files
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`Upload progress for ${file.name}: ${percentCompleted}%`);
            }
          });
          
          console.log('Upload successful:', file.name, response.data);
          successCount++;
          
          // Update progress AFTER successful upload
          setUploadProgress({ current: i + 1, total: uploadFiles.length, fileName: file.name });
          
          // Small delay to show completion
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          console.error('Error details:', error.response?.data);
          failedFiles.push({ name: file.name, error: error.response?.data?.detail || error.message });
        }
      }

      // Show results
      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}!`);
      }
      
      if (failedFiles.length > 0) {
        failedFiles.forEach(f => {
          toast.error(`Failed to upload ${f.name}: ${f.error}`);
        });
      }
      
      // Refresh file list if any succeeded
      if (successCount > 0) {
        await fetchFiles();
        await fetchStats();
      }
      
      // Auto-close dialog after a short delay (1.5 seconds)
      console.log('Upload complete - auto-closing dialog in 1.5 seconds');
      setTimeout(() => {
        closeDialog();
      }, 1500);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Delete ${fileName}?`)) return;

    try {
      await axios.delete(`${API}/personal-files/${fileId}`);
      toast.success('File deleted successfully');
      fetchFiles();
      fetchStats();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
    }
  };

  const handleDownload = (filePath, fileName) => {
    try {
      // Use current domain for file access (works in both preview and production)
      const currentDomain = window.location.origin;
      const fullUrl = `${currentDomain}${filePath}`;
      console.log('Opening file:', fullUrl);
      console.log('Current domain:', currentDomain);
      console.log('File path:', filePath);
      
      // For PDFs and images, open in new tab; for others, force download
      const isPdfOrImage = fileName.toLowerCase().endsWith('.pdf') || 
                           fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|heic)$/);
      
      if (isPdfOrImage) {
        // Open in new tab
        console.log('Opening in new tab:', fullUrl);
        const newWindow = window.open(fullUrl, '_blank');
        if (!newWindow) {
          toast.error('Please allow pop-ups for this site');
        }
      } else {
        // Force download
        console.log('Forcing download:', fullUrl);
        const link = document.createElement('a');
        link.href = fullUrl;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to open file');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image':
        return <ImageIcon className="w-8 h-8 text-blue-400" />;
      case 'pdf':
        return <FileText className="w-8 h-8 text-red-400" />;
      case 'document':
        return <File className="w-8 h-8 text-green-400" />;
      default:
        return <File className="w-8 h-8 text-gray-400" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-orange-400" />
            Personal Files
          </h3>
          {stats && (
            <p className="text-sm text-slate-400 mt-1">
              {stats.total_files} files • {stats.total_size_mb} MB used
            </p>
          )}
        </div>
        <Dialog 
          key={isUploadOpen ? 'open' : 'closed'} 
          open={isUploadOpen} 
          onOpenChange={(open) => {
            console.log('Dialog onOpenChange:', open);
            if (!open && !isUploading) {
              closeDialog();
            } else if (open) {
              setIsUploadOpen(open);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-orange-600">
              <Upload className="w-4 h-4 mr-2" />
              Upload Files
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 text-white border-slate-700 max-w-lg">
            <DialogHeader>
              <DialogTitle>Upload Personal Files</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label htmlFor="personal-files">Select Files</Label>
                <Input
                  id="personal-files"
                  type="file"
                  multiple
                  accept="image/*,application/pdf,.pdf,.doc,.docx,.txt,.xlsx,.xls,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                  required
                  className="mt-1"
                  disabled={isUploading}
                />
                {uploadFiles.length > 0 && (
                  <p className="text-sm text-slate-400 mt-2">
                    {uploadFiles.length} file{uploadFiles.length > 1 ? 's' : ''} selected
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  Supported: Images, PDFs, Documents (max 25MB each)
                </p>
              </div>

              <div>
                <Label htmlFor="file-description">Description (Optional)</Label>
                <Textarea
                  id="file-description"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Add a description for your files..."
                  className="mt-1"
                  disabled={isUploading}
                />
              </div>

              {/* Upload Progress */}
              {isUploading && uploadProgress.total > 0 && (
                <div className="space-y-3 animate-in fade-in-0 duration-300">
                  <div className="p-4 bg-blue-900/50 border-2 border-blue-600 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <Upload className="w-6 h-6 text-blue-300 animate-bounce" />
                      <div className="flex-1">
                        <p className="text-base font-bold text-white">
                          Uploading {Math.min(uploadProgress.current + 1, uploadProgress.total)} of {uploadProgress.total}
                        </p>
                        <p className="text-sm text-blue-200 truncate mt-1">
                          {uploadProgress.fileName}
                        </p>
                      </div>
                      <span className="text-2xl font-bold text-white bg-blue-600 px-3 py-1 rounded-lg">
                        {Math.min(100, Math.round(((uploadProgress.current + 1) / uploadProgress.total) * 100))}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-900 rounded-full h-3 overflow-hidden border border-slate-700">
                      <div 
                        className="bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 h-3 rounded-full transition-all duration-500 ease-in-out animate-pulse"
                        style={{ 
                          width: `${Math.min(100, ((uploadProgress.current + 1) / uploadProgress.total) * 100)}%`,
                          transition: 'width 0.5s ease-in-out'
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-center text-sm text-slate-400 animate-pulse">
                    ⏳ Please wait... Dialog will close automatically when complete
                  </p>
                </div>
              )}

              {!isUploading && (
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Files
                </Button>
              )}
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* File List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <p className="text-slate-400 mt-4">Loading files...</p>
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No files uploaded yet</p>
          <p className="text-sm text-slate-500 mt-2">Upload your first file to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {files.map((file) => (
            <div
              key={file.id}
              className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 hover:border-orange-500 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  {getFileIcon(file.file_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-semibold truncate">{file.file_name}</h4>
                  {file.description && (
                    <p className="text-slate-400 text-sm mt-1 line-clamp-2">{file.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    <span>{formatFileSize(file.file_size)}</span>
                    <span>•</span>
                    <span>{new Date(file.uploaded_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => handleDownload(file.file_path, file.file_name)}
                  size="sm"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Open
                </Button>
                <Button
                  onClick={() => handleDelete(file.id, file.file_name)}
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
